// src/features/books/pages/BookDetailPage.tsx
import React, {
    useEffect, useRef, useState, useMemo, useLayoutEffect, useCallback, forwardRef, useImperativeHandle
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PageShell } from "@/ui/PageShell";
import { withBase, HEADER } from "@/ui/Header";
import { usePageScale } from "@/ui/usePageScale";
import { libraryAdd, libraryExists, bookStoreList, BookStore, api, calendarAdd, calendarExists, calendarUpdate, calendarList, reviewBookStats, reviewMyForBook, isLoggedIn } from "@/lib/api";
import { notify } from "@/ui/notify";
import { registerPager, unregisterPager } from "@/lib/pagerBridge";
import { toCoverUrl } from "@/lib/coverUrl";
import { decodeHtmlText } from "@/lib/decodeHtmlText";
import { ReviewCardModal } from "@/features/readingnote/components/ReviewCardModal";
import { COLOR, FW, BORDER, RADIUS } from "@/ui/tokens";
import { LibrarySheet } from "@/ui/LibrarySheet";
import { ActionSheet } from "@/ui/ActionSheet";
import { androidCallSpecificApp } from "@/lib/androidBridge";


// 파일 상단 유틸 (YYYY-MM-DD)
const isoToday = () => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
};

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

/* ── 공용 ── */
const BASE = {
    W: 1264,
    // Figma(62:1521) 기준 헤더는 1264 좌표계에서 100. 공용 HEADER.H(80)는 원시 px라
    // 스케일을 안 타므로, 이 페이지는 headerHeight prop으로 직접 스케일해서 넘긴다.
    HEADER_H: 100,
    MARGIN_X: 108,
    COVER_TOP: 110,
    BLOCK_W: 1048,
    COVER_H: 409,
    SYN_TOP: 630,
    SYN_H: 525,
    BTN_TOP: 1200,
    BTN_W: 326,
    BTN_H: 124,
    // 독서 상태/구매처 칩 줄 (Figma 62:1550, 62:1556 기준)
    CHIP_TOP: 18,     // 헤더 바로 아래부터
    CHIP_H: 64,
    CHIP_GAP: 20,
};
// 칩 밴드(Figma 62:1529)는 1264 좌표계에서 높이 100. 칩이 붙으면 표지 이하 전부를 그만큼 내린다.
// 밴드 안에서 칩은 위아래 18씩 여백 → 18 + 64 + 18 = 100.
const CHIP_SHIFT = 100;
const BTN_GAP = (BASE.BLOCK_W - 3 * BASE.BTN_W) / 2;
const CANVAS_H = Math.max(BASE.BTN_TOP + BASE.BTN_H, BASE.SYN_TOP + BASE.SYN_H) + 40;

const STATUS_CHIP_LABEL: Record<string, string> = {
    READING:  "읽고 있는 책",
    WISHLIST: "읽고 싶은 책",
    FINISHED: "완독한 책",
};

const ICONS = {
    back: withBase("icons/prev1.svg"),
    prev: withBase("icons/prev1.svg"),
    next: withBase("icons/next1.svg"),
};

/* ── Row ── */
function Row({
                 label, children, fontPx = 36, linePx = 48, labelW = 160, gap = 24, padY = 10
             }: { label: string; children?: React.ReactNode; fontPx?: number; linePx?: number; labelW?: number; gap?: number; padY?: number; }) {
    const px = (n: number) => `${Math.round(n)}px`;
    if (!children) return null;
    return (
        <div style={{ display: "flex", alignItems: "flex-start", paddingTop: px(padY), paddingBottom: px(padY) }}>
            <div style={{ width: px(labelW), fontSize: px(fontPx), lineHeight: px(linePx), flexShrink: 0, color: "rgba(0,0,0,.75)" }}>
                {label}
            </div>
            <div style={{ fontSize: px(fontPx), lineHeight: px(linePx), marginLeft: px(gap), color: COLOR.text }}>
                {children}
            </div>
        </div>
    );
}

/* ── SVG 별 아이콘 ── */
function StarIcon({ filled, size }: { filled: boolean; size: number }) {
    const fill = filled ? "#111" : "#D1D5DB";
    const stroke = filled ? "#111" : "#D1D5DB";
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
            <path
                d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                fill={fill}
                stroke={stroke}
                strokeWidth={2.6}
                strokeLinejoin="round"
            />
        </svg>
    );
}

/* ── 부분 채움 별점 (SVG 버전) ── */
function PartialStars({ value, sizePx }: { value: number; sizePx: number }) {
    const rating = Math.min(5, Math.max(0, value));
    const fullStars = Math.floor(rating);
    const hasHalf = rating - fullStars >= 0.5;

    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: `${Math.round(sizePx * 0.15)}px` }}>
            {[1, 2, 3, 4, 5].map((n) => {
                const filled = n <= fullStars || (n === fullStars + 1 && hasHalf);
                return <StarIcon key={n} filled={filled} size={sizePx} />;
            })}
        </span>
    );
}

/* ── 시놉시스 Pager (기존 유지) ── */
type PagerHandle = { prev: () => void; next: () => void };
type SynopsisPagerProps = { text: string; height: number; fontPx?: number; linePx?: number; padY?: number; padX?: number; onMeta?: (pageIdx: number, total: number) => void; };

const SynopsisPager = forwardRef<PagerHandle, SynopsisPagerProps>(function SynopsisPager(
    { text, height, fontPx = 36, linePx = 48, padY = 20, padX = 0, onMeta }, ref
) {
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    const measurerRef = useRef<HTMLDivElement | null>(null);
    const [pages, setPages] = useState<string[]>([]);
    const [page, setPage] = useState(0);
    const onMetaRef = useRef(onMeta);
    useEffect(() => { onMetaRef.current = onMeta; }, [onMeta]);

    const tokenize = (s: string) => s.split(/(\s+|\n+)/);
    const fitChars = (s: string, maxH: number, measurer: HTMLDivElement) => {
        let lo = 1, hi = Math.max(1, s.length), fit = 0;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            measurer.textContent = s.slice(0, mid);
            if (measurer.scrollHeight <= maxH) { fit = mid; lo = mid + 1; } else hi = mid - 1;
        }
        return fit;
    };
    const computePages = (availH: number, availW: number) => {
        const measurer = measurerRef.current!;
        measurer.style.width = `${availW}px`;
        measurer.style.fontSize = `${fontPx}px`;
        measurer.style.lineHeight = `${linePx}px`;
        const raw = text || "";
        const tokens = tokenize(raw.length ? raw : "시놉시스가 없습니다.");
        const result: string[] = [];
        let buf = "";
        for (let i = 0; i < tokens.length; i++) {
            const tk = tokens[i];
            measurer.textContent = buf + tk;
            if (measurer.scrollHeight <= availH) { buf += tk; continue; }
            if (buf.trim().length > 0) { result.push(buf.trimEnd()); buf = ""; i--; }
            else {
                const fit = fitChars(tk, availH, measurer);
                if (fit > 0) { result.push(tk.slice(0, fit)); tokens[i] = tk.slice(fit); i--; }
                else { result.push(tk.slice(0, 1)); tokens[i] = tk.slice(1); i--; }
            }
        }
        if (buf.length) result.push(buf.trimEnd());
        return result.length ? result : [""];
    };

    useLayoutEffect(() => {
        const el = wrapRef.current!;
        if (!measurerRef.current) {
            const m = document.createElement("div");
            m.style.position = "absolute";
            m.style.visibility = "hidden";
            m.style.pointerEvents = "none";
            m.style.left = "-99999px";
            m.style.top = "-99999px";
            m.style.whiteSpace = "pre-wrap";
            m.style.wordBreak = "break-word";
            document.body.appendChild(m);
            measurerRef.current = m;
        }

        const doCompute = () => {
            const availW = Math.max(0, el.clientWidth - padX * 2);
            const availH = Math.max(0, height - padY * 2);
            const arr = computePages(availH, availW);
            setPages(arr);
            setPage(0);
            try { (listRef.current as any)?.scrollTo?.({ left: 0 }); } catch { if (listRef.current) listRef.current.scrollLeft = 0; }
            onMetaRef.current?.(0, arr.length);
        };

        doCompute();

        const RO: any = (window as any).ResizeObserver;
        let ro: any = null;
        if (RO) { ro = new RO(doCompute); ro.observe(el); }
        else {
            const on = () => doCompute();
            window.addEventListener("resize", on);
            return () => window.removeEventListener("resize", on);
        }
        return () => {
            if (ro) ro.disconnect();
            if (measurerRef.current) {
                  try { document.body.removeChild(measurerRef.current); } catch {}
                  measurerRef.current = null;
                }
            };
    }, [text, height, fontPx, linePx, padY, padX]);

    useEffect(() => {
        const el = listRef.current; if (!el) return;
        const onScroll = () => {
            const idx = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
            if (idx !== page) { setPage(idx); onMetaRef.current?.(idx, Math.max(1, pages.length || 1)); }
        };
        el.addEventListener("scroll", onScroll as any, { passive: true } as any);
        return () => el.removeEventListener("scroll", onScroll as any);
    }, [page, pages.length]);

    const go = (to: number) => {
        const el = listRef.current; if (!el) return;
        const n = Math.max(0, Math.min(to, pages.length - 1));
        setPage(n);
        const x = n * el.clientWidth;
        try { (el as any).scrollTo({ left: x, behavior: "auto" }); } catch { el.scrollLeft = x; }
        // behavior: "auto"일 때 스크롤 이벤트가 발생하지 않을 수 있으므로 직접 메타 업데이트
        onMetaRef.current?.(n, pages.length);
    };
    useImperativeHandle(ref, () => ({ prev: () => go(page - 1), next: () => go(page + 1) }), [page, pages.length]);

    return (
        <div ref={wrapRef} style={{ height, borderRadius: 12, overflow: "hidden" }}>
            <div
                ref={listRef}
                style={{
                    height: "100%", width: "100%", display: "grid",
                    gridAutoFlow: "column", gridAutoColumns: "100%",
                    overflowX: "auto", overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "none" as any,
                    msOverflowStyle: "none" as any,
                    scrollSnapType: "x mandatory",
                }}
            >
                {(pages.length ? pages : ["시놉시스가 없습니다."]).map((p, i) => (
                    <div key={i} style={{ scrollSnapAlign: "start", padding: `${padY}px ${padX}px`, fontSize: fontPx, lineHeight: `${linePx}px`, whiteSpace:"pre-wrap", wordBreak:"break-word", color:COLOR.text }}>
                        {p || "시놉시스가 없습니다."}
                    </div>
                ))}
            </div>
        </div>
    );
});

function pad2(n:number){return String(n).padStart(2,'0');}
function ymd(d:Date){return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;}
function startOfMonth(d:Date){return new Date(d.getFullYear(), d.getMonth(), 1);}
function endOfMonth(d:Date){return new Date(d.getFullYear(), d.getMonth()+1, 0);}
function buildMatrix(base:Date, weekStartsOn:0|1=0){
    const first=startOfMonth(base), last=endOfMonth(base);
    const firstW=(first.getDay()-weekStartsOn+7)%7, total=last.getDate();
    const cells: (Date|null)[] = [];
    for(let i=0;i<firstW;i++) cells.push(null);     // 앞쪽 빈칸
    for(let d=1; d<=total; d++) cells.push(new Date(base.getFullYear(), base.getMonth(), d));
    while(cells.length%7) cells.push(null);         // 끝쪽 빈칸
    const rows: (Date|null)[][]=[]; for(let r=0;r<cells.length/7;r++) rows.push(cells.slice(r*7,r*7+7)); return rows;
}

function CalendarPickModal({
                               open, currentBookId, currentDate, onPick, onClose, s, spx, sfpx
                           }:{
    open:boolean;
    currentBookId:number;
    currentDate?:string|null;
    onPick:(iso:string)=>void;
    onClose:()=>void;
    s:(n:number)=>number; spx:(n:number)=>string; sfpx:(n:number)=>string;
}){
    const [month,setMonth]=useState<Date>(()=>startOfMonth(currentDate?new Date(currentDate):new Date()));
    const [disabled,setDisabled]=useState<Set<string>>(new Set());
    const [selected,setSelected]=useState<string>(currentDate||ymd(new Date()));
    const loadTaken = useCallback(async(d:Date)=>{
        const ym = `${d.getFullYear()}-${pad2(d.getMonth()+1)}`;
        const r = await calendarList(ym); // { items:[{date,covers:[{book_id,...}]}] }
        const taken = new Set<string>();
        for (const it of (r.items||[])) {
            const usedByOthers = (it.covers||[]).some((c:any)=> String(c.book_id) !== String(currentBookId));
            if (usedByOthers) taken.add(it.date);
        }
        // 내 날짜는 허용(수정 용이)
        setDisabled(taken);
    },[currentBookId]);

    useEffect(()=>{ if(open) loadTaken(month); },[open, month, loadTaken]);

    if (!open) return null;
    const rows = buildMatrix(month, 0);

    const handlePrevMonth = () => {
        setMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    };
    const handleNextMonth = () => {
        setMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2000,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: "100%",
                    maxWidth: spx(1080),             // 전체 폭
                    margin: "0 16px",
                    background: COLOR.white,
                    borderRadius: 24,              // 둥근 모서리
                    padding: spx(28),
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    rowGap: spx(16),
                }}
            >
                {/* 상단: 월 네비게이션 */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: spx(200),
                        marginBottom: spx(4),
                    }}
                >
                    <button
                        type="button"
                        onClick={handlePrevMonth}
                        style={{
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            padding: spx(8),
                            fontSize: sfpx(32),
                            lineHeight: 1,
                        }}
                        aria-label="이전 달"
                    >
                        {"<"}
                    </button>

                    <div
                        style={{
                            fontWeight: 800,
                            fontSize: sfpx(34),
                        }}
                    >
                        {month.getFullYear()}.{pad2(month.getMonth() + 1)}
                    </div>

                    <button
                        type="button"
                        onClick={handleNextMonth}
                        style={{
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            padding: spx(8),
                            fontSize: sfpx(32),
                            lineHeight: 1,
                        }}
                        aria-label="다음 달"
                    >
                        {">"}
                    </button>
                </div>

                {/* 요일 헤더 */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(7, 1fr)",
                        marginBottom: spx(8),
                        fontSize: sfpx(20),
                        fontWeight: 700,
                        textAlign: "center",
                    }}
                >
                    {["일", "월", "화", "수", "목", "금", "토"].map((d) => (
                        <div key={d}>{d}</div>
                    ))}
                </div>

                {/* 날짜 그리드 */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateRows: `repeat(${rows.length}, ${spx(96)})`,
                        rowGap: spx(4),
                    }}
                >
                    {rows.map((week, wi) => (
                        <div
                            key={wi}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(7, 1fr)",
                            }}
                        >
                            {week.map((d, di) => {
                                if (!d) {
                                    return (
                                        <div
                                            key={`${wi}-${di}`}
                                            style={{ height: spx(48) }}
                                        />
                                    );
                                }
                                const iso = ymd(d);
                                const isDisabled = disabled.has(iso);
                                const isSel = selected === iso;

                                return (
                                    <button
                                        key={iso}
                                        type="button"
                                        onClick={() => {
                                            if (!isDisabled) setSelected(iso);
                                        }}
                                        disabled={isDisabled}
                                        style={{
                                            border: "none",
                                            background: "transparent",
                                            cursor: isDisabled ? "default" : "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: 0,
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: spx(60),
                                                height: spx(60),
                                                borderRadius: 999,             // ● 동그라미
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: sfpx(36),
                                                fontWeight: FW.extraBold,
                                                backgroundColor: isSel ? COLOR.black : "transparent",
                                                color: isSel
                                                    ? COLOR.white
                                                    : isDisabled
                                                        ? "#ccc"
                                                        : COLOR.text,
                                            }}
                                        >
                                            {d.getDate()}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* 하단 버튼들 */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: spx(12),
                        marginTop: spx(20),
                    }}
                >
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            minWidth: spx(178),
                            height: spx(72),
                            borderRadius: 5,
                            border: BORDER.darkText,
                            background: COLOR.white,
                            fontSize: sfpx(36),
                            fontWeight: FW.bold,
                            cursor: "pointer",
                        }}
                    >
                        취소
                    </button>
                    <button
                        type="button"
                        onClick={() => onPick(selected)}
                        style={{
                            minWidth: spx(178),
                            height: spx(72),
                            borderRadius: 5,
                            border: "none",
                            background: COLOR.black,
                            color: COLOR.white,
                            fontSize: sfpx(36),
                            fontWeight: FW.bold,
                            cursor: "pointer",
                        }}
                    >
                        확인
                    </button>
                </div>
            </div>
        </div>
    );
}



/* ── 상세 상단의 독서 상태 / 구매처 칩. onClick이 없으면 표시 전용이다 ── */
function InfoChip({ label, filled = false, px, sfpx, onClick }:{
    label:string; filled?:boolean;
    px:(n:number)=>string; sfpx:(n:number)=>string;
    onClick?:() => void;
}) {
    return (
        <div
            onClick={onClick}
            role={onClick ? "button" : undefined}
            style={{
                height: px(BASE.CHIP_H), padding: `0 ${px(28)}`,
                display:"flex", alignItems:"center", justifyContent:"center",
                borderRadius: 999, whiteSpace:"nowrap",
                fontSize: sfpx(30), fontWeight: FW.bold, lineHeight: 1,
                background: filled ? COLOR.text : COLOR.white,
                color:      filled ? COLOR.white : COLOR.text,
                border: filled ? `1px solid ${COLOR.text}` : BORDER.muted,
                cursor: onClick ? "pointer" : "default",
            }}
        >
            {label}
        </div>
    );
}

/* ── 상세 페이지 ── */
export default function BookDetailPage() {
    const { r, s, px, sf, sfpx, widthPx } = usePageScale();
    const spx = px;

    const { id } = useParams();
    const nav = useNavigate();
    const loc = useLocation() as any;

    const rawId = id || "";
    const normId = rawId.startsWith("db-") ? rawId.slice(3) : rawId;

    const preset = loc.state?.book;
    const hideButtons = !!loc.state?.hideButtons;     // ✅ 내 서재에서 진입 시 버튼 숨김

    // ✅ 추가: 리딩노트에서 넘겨준 상태값
    const reviewOverride: string | undefined = loc.state?.overrideSynopsis; // 본문 대체
    const [myReviewId, setMyReviewId] = useState<number | undefined>(loc.state?.myReviewId);
    const [hasMyReview, setHasMyReview] = useState<boolean>(!!(loc.state?.hasMyReview || loc.state?.myReviewId));
    const myKeywords: string[] = loc.state?.myKeywords || [];
    const myRating: number = loc.state?.myRating || 0;
    const [myCreatedAt, setMyCreatedAt] = useState<string>(loc.state?.myCreatedAt || "");

    // 카드 저장 모달
    const [cardModalOpen, setCardModalOpen] = useState(false);
    const cardNickname = (() => {
        try {
            const u = JSON.parse(localStorage.getItem("bt.user") || "{}");
            return u.nickname || u.name || "";
        } catch { return ""; }
    })();

    // ✅ 추가: 섹션 타이틀 결정
    const sectionTitle = reviewOverride ? "나의 리뷰" : "책 소개";

    const looksLikeIsbn = useMemo(
        () => !!normId && (/^\d{13}$/.test(normId) || /^[0-9Xx]{10}$/.test(normId)),
        [normId]
    );
    const [book, setBook] = useState<any>(preset || null);
    // preset이 있으면 fetch 안 함 (리뷰 수정 후 돌아올 때 등)
    const needsFetch = !!normId && !(preset?.synopsis || preset?.description);

    const [loading, setLoading] = useState<boolean>(needsFetch);
    const [error, setError] = useState<string | null>(null);

    // 날짜 선택 모달 상태
    const [dateOpen, setDateOpen] = useState(false);
    const [pickDate, setPickDate] = useState<string>(isoToday());

    // 달력 등록 여부 상태
        const [calAssigned, setCalAssigned] = useState<{assigned:boolean; date?:string|null}>({ assigned:false, date:null });

    // 달력 저장 중복 제출 가드 (더블 탭 방지)
    const calSavingRef = useRef(false);

    // 서재 등록 여부 상태
    const [inLibrary, setInLibrary] = useState(hideButtons); // 서재에서 진입하면 이미 true
    // 상단 칩에 쓸 독서 상태/구매처. 구매처는 어떤 경로로든 NULL일 수 있다(달력 경유 등).
    const [libInfo, setLibInfo] = useState<{ status: string|null; store: BookStore|null }>({ status: null, store: null });

    // 별점 표시용 상태
    const [ratingInfo, setRatingInfo] = useState<{avg?:number|null; count?:number; myRating?:number|null}>({});

    // 상세 진입 시(또는 book.id 생기면) 달력/서재 등록 여부 확인
        useEffect(() => {
          (async () => {
           try {
                 if (!book?.id) return;
                 // 서재에서 진입해도(hideButtons) 호출한다 — 상단 칩에 쓸 상태/구매처가 필요하다.
                 const [cal, lib] = await Promise.all([
                     calendarExists(Number(book.id)),
                     libraryExists(Number(book.id)),
                 ]);
                 setCalAssigned({ assigned: !!cal.assigned, date: cal.date || null });
                 setInLibrary(lib.in_library || hideButtons);
                 setLibInfo({ status: lib.status, store: lib.store });
               } catch {/* 존재 확인 실패는 무시 */}
           })();
        }, [book?.id]);

    // 별점 정보 + 내 리뷰 여부 로드
    // book이 있지만 id가 없으면(검색 외부 API 결과) ensure로 DB 등록 후 조회
    useEffect(() => {
        if (!book) return;
        let cancelled = false;
        (async () => {
            let bId = book.id ? Number(book.id) : 0;
            // DB id가 없으면 ensure 호출
            if (!bId) {
                try {
                    const r = await api('/books/ensure', { json: {
                        isbn13: book.isbn13, isbn10: book.isbn10,
                        title: book.title, title_plain: book.title_plain,
                        authors: book.authors, publisher: book.publisher,
                        synopsis: book.synopsis, pub_date: book.pub_date,
                        cover_url: book.cover_url, cover_source: book.cover_source,
                        source: book.source, source_meta: book.source_meta,
                        genre: book.genre, work_key: book.work_key
                    }});
                    if (cancelled) return;
                    bId = r.id as number;
                    (book as any).id = bId;
                } catch { return; }
            }
            if (!bId || cancelled) return;
            // Check library status after we have a definite bId (handles search-result books)
            try {
                const lib = await libraryExists(bId);
                if (!cancelled) {
                    setInLibrary(lib.in_library || hideButtons);
                    setLibInfo({ status: lib.status, store: lib.store });
                }
            } catch { /* 무시 */ }
            try {
                const [cal] = await Promise.all([calendarExists(bId)]);
                if (!cancelled) setCalAssigned({ assigned: !!cal.assigned, date: cal.date || null });
            } catch { /* 무시 */ }
            try {
                const stats: any = await reviewBookStats(bId);
                if (!cancelled) setRatingInfo(prev => ({ ...prev, avg: stats?.avg_rating ?? null, count: stats?.count ?? 0 }));
            } catch { /* 무시 */ }
            try {
                const my: any = await reviewMyForBook(bId);
                if (!cancelled) {
                    setRatingInfo(prev => ({ ...prev, myRating: my?.rating ?? null }));
                    setHasMyReview(true);
                    setMyReviewId(my?.id);
                    if (my?.created_at) setMyCreatedAt(my.created_at);
                }
            } catch {
                if (!cancelled) {
                    setHasMyReview(false);
                    setMyReviewId(undefined);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [book?.id, book?.isbn13, book?.title]);

    // 달력 등록: 필요한 API 엔드포인트로 교체하세요.
    const addToCalendar = useCallback(async (overrideDate?: string) => {
        // 원인2: 응답 전 더블 탭으로 요청이 두 번 나가는 것 방지
        if (calSavingRef.current) return;
        calSavingRef.current = true;
        try {
            const bId = await ensureBookId();
            if (!bId) return;
            const useDate = overrideDate ?? pickDate;

            // 원인1: add/update 분기가 stale한 calAssigned에 의존 → 어긋나면 자동 복구
            let action: 'add' | 'update' = calAssigned.assigned ? 'update' : 'add';
            try {
                if (action === 'update') await calendarUpdate(bId, useDate);
                else                      await calendarAdd(bId, useDate);
            } catch (e:any) {
                const code = e?.code || e?.json?.error;
                if (code === 'ALREADY_IN_CALENDAR') {
                    // 이미 달력에 있는 책인데 상태가 false로 남아 add로 들어온 경우 → update로 복구
                    await calendarUpdate(bId, useDate);
                    action = 'update';
                } else if (e?.status === 404) {
                    // 달력에 없는 책인데 상태가 true로 남아 update로 들어온 경우 → add로 복구
                    await calendarAdd(bId, useDate);
                    action = 'add';
                } else {
                    throw e;
                }
            }

            setCalAssigned({ assigned:true, date: useDate });
            setPickDate(useDate);
            setDateOpen(false);
            notify(action === 'update' ? "달력 날짜를 수정했어요." : "달력에 추가됐어요!");
        } catch (e:any) {
            const status = e?.status;
            const code   = e?.code || e?.json?.error;
            const msg    = String(e?.message || "");
            const body   = String(e?.body || "");

            // 다른 책이 그 날짜를 이미 점유 중
            if (code === 'DATE_TAKEN' || /unique|duplicate/i.test(msg + body)) {
                notify("그 날짜는 이미 다른 책이 등록되어 있어요. 다른 날짜를 선택해 주세요.");
                return;
            }
            // 토큰 만료/미로그인
            if (status === 401) {
                notify("로그인이 필요해요. 다시 로그인해 주세요.");
                return;
            }
            console.error("calendar save error", { status, code, msg, body: e?.body });
            notify(calAssigned.assigned ? "수정에 실패했습니다." : "추가에 실패했습니다.");
        } finally {
            calSavingRef.current = false;
        }
        }, [pickDate, calAssigned.assigned]);


    useEffect(() => {
        if (!needsFetch) return
        let aborted = false;
        (async () => {
            try {
                setLoading(true); setError(null);
                const endpoint = looksLikeIsbn
                    ? `${API_BASE}/books/isbn/${normId}`
                    : `${API_BASE}/books/by-id/${normId}`;
                const r = await fetch(endpoint, { headers: { Accept: "application/json" } });
                const ctype = r.headers.get("content-type") || "";
                if (!r.ok || !ctype.includes("application/json")) { const t = await r.text(); throw new Error(`API ${r.status}: ${t.slice(0,160)}`); }
                const data = await r.json();
                if (!aborted) setBook(data.book || data);
            } catch (e:any) {
                if (!aborted) setError(e?.message || "상세 로딩 실패");
            } finally { if (!aborted) setLoading(false); }
        })();
        return () => { aborted = true; };
    }, [needsFetch, normId, looksLikeIsbn]);

    if (error) {
        return (
            <div style={{ padding: 24 }}>
                <button onClick={() => nav(-1)} style={{ border:"1px solid #ddd", borderRadius:8, padding:"6px 12px", fontSize: sfpx(36), background:"#fff", cursor:"pointer" }}>
                    ← 상세 정보
                </button>
                <div style={{ color:"#dc2626", fontSize: sfpx(36), marginTop: 12 }}>{error}</div>
            </div>
        );
    }

    const titlePlain = book?.title_plain || book?.title || "";
    const pagerRef = useRef<PagerHandle>(null);
    const [pageMeta, setPageMeta] = useState({ page: 1, total: 1 });
    const handleMeta = useCallback((idx: number, total: number) => { setPageMeta({ page: idx + 1, total }); }, []);

    // 물리키 페이저 브릿지 등록
    useEffect(() => {
        registerPager({
            next: () => pagerRef.current?.next(),
            prev: () => pagerRef.current?.prev(),
        });
        return () => unregisterPager();
    }, []);

    const coverW = Math.round(BASE.COVER_H * 0.75);
    const infoW = BASE.BLOCK_W - coverW;

    // 위치 보정
    const OFF = BASE.HEADER_H;
    // 서재에 담긴 책만 상단에 독서 상태/구매처 칩을 띄운다. 칩이 붙으면 아래 전부를 그만큼 내린다.
    const statusLabel = libInfo.status ? STATUS_CHIP_LABEL[libInfo.status] : null;
    const showChips = inLibrary && !!statusLabel;
    const shift = showChips ? CHIP_SHIFT : 0;
    const topCover = Math.max(0, BASE.COVER_TOP + shift - OFF);
    const topSyn   = Math.max(0, BASE.SYN_TOP + shift - OFF);
    const topBtn   = Math.max(0, BASE.BTN_TOP + shift - OFF);
    const bodyMinH = Math.max(0, CANVAS_H + shift - OFF);

    // 연동할 앱이 등록된 구매처만 칩을 누를 수 있다(어드민에서 지정).
    // 같은 구매처가 일반 빌드와 e-ink 빌드를 따로 내는 경우가 있어 패키지명이 둘이다.
    const storePkg = libInfo.store?.android_package || undefined;
    const storePkgEink = libInfo.store?.android_package_eink || undefined;
    const [appPickOpen, setAppPickOpen] = useState(false);

    // 브리지가 없을 때(브라우저)만 안내한다. 앱 안에서는 실행 실패를
    // 여기서 알 수 없으므로 안드로이드가 안내해야 한다.
    const launchStoreApp = (pkg: string) => {
        if (!androidCallSpecificApp(pkg)) notify("앱에서만 열 수 있습니다.");
    };

    // 둘 다 등록돼 있을 때만 고르게 한다. 하나뿐이면 시트를 띄우는 게 손만 더 가는 일이다.
    const onlyPkg = storePkg ?? storePkgEink;
    const openStoreApp = onlyPkg
        ? () => {
            if (storePkg && storePkgEink) { setAppPickOpen(true); return; }
            launchStoreApp(onlyPkg);
        }
        : undefined;

    /* ── 서재 추가 바텀시트 ── */
    const [sheetOpen, setSheetOpen] = useState(false);
    const openSheet = () => {
        if (!isLoggedIn()) {
            nav("/auth/need-login", { state: { from: location.pathname, featureName: "서재" } });
            return;
        }
        setSheetOpen(true);
        loadStores();
    };
    const closeSheet = () => setSheetOpen(false);

    /* ── 구매처 목록 (서재 추가 시트에서 사용) ── */
    const [stores, setStores] = useState<BookStore[]>([]);
    const [storesLoading, setStoresLoading] = useState(false);
    const storesLoadedRef = useRef(false);

    // 메인/더보기를 모두 받아둔다. 접었다 펴는 건 시트가 알아서 한다.
    const loadStores = () => {
        if (storesLoadedRef.current) return;
        storesLoadedRef.current = true;
        setStoresLoading(true);
        bookStoreList()
            .then(r => setStores(r.stores || []))
            // 구매처 목록을 못 불러와도 구매처 없이 서재 추가 자체는 진행할 수 있어야 한다.
            .catch(() => { storesLoadedRef.current = false; })
            .finally(() => setStoresLoading(false));
    };

    const addAndGo = async (status:'READING'|'WISHLIST'|'FINISHED', storeId: number | null) => {
        if (!book) return;
        // 상세에서 확보한 book 정보를 그대로 전달 (백엔드에서 upsert)
        await libraryAdd(status, {
            isbn13: book.isbn13, isbn10: book.isbn10,
            title: book.title, title_plain: book.title_plain,
            authors: book.authors, publisher: book.publisher,
            synopsis: book.synopsis, pub_date: book.pub_date,
            cover_url: book.cover_url, cover_source: book.cover_source,
            source: book.source, source_meta: book.source_meta,
            genre: book.genre
        }, storeId);
        nav("/library", { replace: true, state: { goto: status } });
    };

    const ensureBookId = async (): Promise<number> => {
        if (!book) return 0;
        if (book.id) return book.id;
        const r = await api('/books/ensure', { json: {
                isbn13: book.isbn13, isbn10: book.isbn10,
                title: book.title, title_plain: book.title_plain,
                authors: book.authors, publisher: book.publisher,
                synopsis: book.synopsis, pub_date: book.pub_date,
                cover_url: book.cover_url, cover_source: book.cover_source,
                source: book.source, source_meta: book.source_meta,
                genre: book.genre, work_key: book.work_key
            }});
        (book as any).id = r.id;
        return r.id as number;
    };

    // 나의 리뷰 진입 시 카드 저장 버튼
    const cardSaveBtn = (hasMyReview && reviewOverride) ? (
        <button
            onClick={() => setCardModalOpen(true)}
            style={{
                background: COLOR.text,
                color: COLOR.white,
                border: "none",
                borderRadius: RADIUS.sm,
                padding: `${spx(10)} ${spx(18)}`,
                fontSize: sfpx(22),
                fontWeight: FW.bold,
                cursor: "pointer",
                whiteSpace: "nowrap",
            }}
        >
            카드 저장하기
        </button>
    ) : undefined;

    return (
        <PageShell
            title="상세 정보"
            onBack={() => nav(-1)}
            backIcon={ICONS.back}
            width={widthPx}
            // PageShell은 headerHeight를 원시 px로 쓴다. 본문은 spx로 줄어드는데 헤더만
            // 고정이면 기기 폭에 따라 간격이 어긋나므로 여기서 미리 스케일해서 넘긴다.
            headerHeight={s(BASE.HEADER_H)}
            headerRight={cardSaveBtn}
        >
            <div style={{ gridArea:"body", position:"relative", minHeight: spx(bodyMinH) }}>
                {/* 독서 상태 / 구매처 — 표시 전용. 구매처가 미지정이면 상태 칩만 나온다. */}
                {showChips && (
                    <div style={{ position:"absolute", left: spx(BASE.MARGIN_X), top: spx(BASE.CHIP_TOP),
                                  display:"flex", gap: spx(BASE.CHIP_GAP) }}>
                        <InfoChip label={statusLabel!} filled px={spx} sfpx={sfpx} />
                        {libInfo.store && <InfoChip label={libInfo.store.name} px={spx} sfpx={sfpx} onClick={openStoreApp} />}
                    </div>
                )}

                {/* 표지 + 정보 */}
                <div style={{ position:"absolute", left: spx(BASE.MARGIN_X), top: spx(topCover), width: spx(BASE.BLOCK_W), height: spx(BASE.COVER_H) }}>
                    <div style={{ width:"100%", height:"100%", display:"flex" }}>
                        <div style={{ width: spx(coverW), height: spx(BASE.COVER_H), overflow:"hidden", background:COLOR.bgLighter }}>
                            {book?.cover_url ? (
                                <img src={toCoverUrl(book.cover_url)} alt={titlePlain || "표지"} style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
                            ) : (
                                <div style={{ width:"100%", height:"100%", display:"grid", placeItems:"center", color:"rgba(0,0,0,.4)" }}>표지 없음</div>
                            )}
                        </div>

                        <div style={{ width: spx(infoW), height: spx(BASE.COVER_H), padding: spx(20), borderTopRightRadius:0, borderBottomRightRadius:0, border:BORDER.light, borderLeftWidth:0, background:"#F5F5F5", boxSizing:"border-box" }}>
                            {(() => {
                                // 제목 길이에 따라 폰트 크기 동적 조절
                                const title = loading ? "불러오는 중…" : (titlePlain || "제목 없음");
                                const len = title.length;
                                // 30자 이하: 40px, 50자 이상: 32px, 그 사이 선형 보간
                                const baseFontSize = len <= 30 ? 40 : len >= 50 ? 32 : 40 - ((len - 30) / 20) * 8;
                                const lineHeight = baseFontSize * 1.3;
                                return (
                                    <div style={{
                                        fontSize: sfpx(baseFontSize),
                                        lineHeight: spx(lineHeight),
                                        marginBottom: spx(32),
                                        fontWeight: FW.extraBold,
                                    }}>
                                        {title}
                                    </div>
                                );
                            })()}
                            <Row label="저자"   fontPx={sf(36)} linePx={s(48)} labelW={s(160)} gap={s(24)} padY={s(10)}>{book?.authors}</Row>
                            <Row label="출판사" fontPx={sf(36)} linePx={s(48)} labelW={s(160)} gap={s(24)} padY={s(10)}>{book?.publisher}</Row>
                            <Row label="출판일" fontPx={sf(36)} linePx={s(48)} labelW={s(160)} gap={s(24)} padY={s(10)}>{book?.pub_date}</Row>
                            {hideButtons ? (
                                <Row label="내 별점" fontPx={sf(36)} linePx={s(48)} labelW={s(160)} gap={s(24)} padY={s(10)}>
                                    {ratingInfo.myRating != null
                                        ? <span style={{ display: "inline-flex", alignItems: "center", gap: `${s(4)}px` }}>
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <StarIcon key={n} filled={n <= ratingInfo.myRating!} size={s(32)} />
                                            ))}
                                          </span>
                                        : <span style={{ color: "#999" }}>리뷰 미작성</span>
                                    }
                                </Row>
                            ) : (
                                <Row label="별점" fontPx={sf(36)} linePx={s(48)} labelW={s(160)} gap={s(24)} padY={s(10)}>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: `${s(8)}px` }}>
                                        <PartialStars value={ratingInfo.avg ?? 0} sizePx={s(36)} />
                                        <span>({ratingInfo.count ?? 0}개)</span>
                                    </span>
                                </Row>
                            )}
                        </div>
                    </div>
                </div>

                {/* 책 소개 + 페이지 컨트롤 */}
                <div style={{ position:"absolute", left: spx(BASE.MARGIN_X), top: spx(topSyn), width: spx(BASE.BLOCK_W), height: spx(BASE.SYN_H) }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: spx(8) }}>
                        <div style={{ fontSize: sfpx(36), fontWeight:FW.extraBold }}>{sectionTitle}</div>
                        <div style={{ display:"flex", alignItems:"center" }}>
                            <button onClick={() => pagerRef.current?.prev()} aria-label="이전" disabled={pageMeta.page <= 1}
                                    style={{ width: spx(48), height: spx(48), lineHeight:0, borderRadius:8, display:"grid", placeItems:"center", background:"rgba(255,255,255,.8)", border:BORDER.light, cursor: pageMeta.page <= 1 ? "default" : "pointer", opacity: pageMeta.page <= 1 ? 0.4 : 1 }}>
                                <img src={ICONS.prev} alt="" style={{ width: spx(40), height: spx(40), display:"block" }} />
                            </button>
                            <div style={{ fontSize: sfpx(36), marginLeft: spx(24), marginRight: spx(24), fontFeatureSettings:"'tnum' 1" }}>
                                {pageMeta.page} / {pageMeta.total}
                            </div>
                            <button onClick={() => pagerRef.current?.next()} aria-label="다음" disabled={pageMeta.page >= pageMeta.total}
                                    style={{ width: spx(48), height: spx(48), lineHeight:0, borderRadius:8, display:"grid", placeItems:"center", background:"rgba(255,255,255,.8)", border:BORDER.light, cursor: pageMeta.page >= pageMeta.total ? "default" : "pointer", opacity: pageMeta.page >= pageMeta.total ? 0.4 : 1 }}>
                                <img src={ICONS.next} alt="" style={{ width: spx(40), height: spx(40), display:"block" }} />
                            </button>
                        </div>
                    </div>

                    <SynopsisPager
                        ref={pagerRef}
                        text={reviewOverride?.trim() ?? decodeHtmlText(book?.synopsis || book?.description)}
                        height={s(reviewOverride && myKeywords.length > 0 ? BASE.SYN_H - 280 : BASE.SYN_H - 60)}
                        fontPx={sf(36)}
                        linePx={s(48)}
                        padY={s(20)}
                        padX={0}
                        onMeta={handleMeta}
                    />

                    {/* 나의 리뷰일 때 키워드 표시 */}
                    {reviewOverride && myKeywords.length > 0 && (
                        <div style={{ marginTop: spx(20) }}>
                            <div style={{ fontSize: sfpx(36), fontWeight: FW.extraBold, color: COLOR.text, marginBottom: spx(14) }}>
                                키워드 리뷰
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: spx(12) }}>
                                {myKeywords.map((kw, i) => (
                                    <span
                                        key={i}
                                        style={{
                                            display: "inline-block",
                                            padding: `${spx(10)} ${spx(20)}`,
                                            fontSize: sfpx(26),
                                            fontWeight: 700,
                                            borderRadius: 999,
                                            border: BORDER.dark,
                                            background: COLOR.white,
                                            color: COLOR.black,
                                            whiteSpace: "nowrap",
                                            lineHeight: 1.3,
                                        }}
                                    >{kw}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* 하단 버튼들 */}
                {!hideButtons ? (
                    // ✅ 일반 진입: 기존 3버튼 유지
                    <div
                        style={{
                            position: "absolute",
                            left: spx(BASE.MARGIN_X),
                            top: spx(topBtn),
                            width: spx(BASE.BLOCK_W),
                            height: spx(BASE.BTN_H),
                        }}
                    >
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: `${spx(BASE.BTN_W)} ${spx(BASE.BTN_W)} ${spx(BASE.BTN_W)}`,
                                gridColumnGap: spx(BTN_GAP),
                                height: "100%",
                            }}
                        >
                            {/* 리뷰 보기 */}
                            <button
                                onClick={async () => {
                                    const id = await ensureBookId();
                                    if (id) nav(`/books/${id}/reviews`);
                                }}
                                style={{
                                    height: "100%", fontSize: sfpx(40), fontWeight: FW.extraBold, borderRadius: RADIUS.md,
                                    border: BORDER.light, background: COLOR.white, color: COLOR.text, cursor: "pointer",
                                }}
                            >리뷰 보기</button>

                            {/* 리뷰 쓰기/수정 */}
                            {hasMyReview ? (
                                <button
                                    onClick={() => {
                                        if (!isLoggedIn()) {
                                            nav("/auth/need-login", { state: { from: location.pathname, featureName: "리뷰" } });
                                            return;
                                        }
                                        if (myReviewId) nav(`/reviews/${myReviewId}/edit`, { state: { backTo: "/readingnote" } });
                                    }}
                                    style={{
                                        height: "100%", fontSize: sfpx(40), fontWeight: FW.extraBold, borderRadius: RADIUS.md, transform: "translateZ(0)",
                                        border: BORDER.light, background: COLOR.white, color: COLOR.text, cursor: "pointer",
                                    }}
                                >리뷰 수정</button>
                            ) : (
                                <button
                                    onClick={async () => {
                                        if (!isLoggedIn()) {
                                            nav("/auth/need-login", { state: { from: location.pathname, featureName: "리뷰" } });
                                            return;
                                        }
                                        const id = await ensureBookId();
                                        if (id) nav(`/reviews/new?bookId=${id}`);
                                    }}
                                    style={{
                                        height: "100%", fontSize: sfpx(40), fontWeight: FW.extraBold, borderRadius: RADIUS.md, transform: "translateZ(0)",
                                        border: BORDER.light, background: COLOR.white, color: COLOR.text, cursor: "pointer",
                                    }}
                                >리뷰 쓰기</button>
                            )}

                            {inLibrary ? (
                                // 이미 서재에 있는 책 → 달력 추가/수정
                                <button
                                    onClick={async () => {
                                        if (!isLoggedIn()) {
                                            nav("/auth/need-login", { state: { from: location.pathname, featureName: "달력" } });
                                            return;
                                        }
                                        const bId = await ensureBookId();
                                        if (!bId) return;
                                        setPickDate(calAssigned.date || isoToday());
                                        setDateOpen(true);
                                    }}
                                    style={{
                                        height: "100%", fontSize: sfpx(40), fontWeight: FW.extraBold, borderRadius: RADIUS.md, transform: "translateZ(0)",
                                        border: BORDER.dark, background: COLOR.black, color: COLOR.white, cursor: "pointer",
                                    }}
                                >{calAssigned.assigned ? "달력 수정" : "달력 추가"}</button>
                            ) : (
                                // 서재에 없는 책 → 서재에 추가
                                <button
                                    onClick={openSheet}
                                    style={{
                                        height: "100%", fontSize: sfpx(40), fontWeight: FW.extraBold, borderRadius: RADIUS.md, transform: "translateZ(0)",
                                        border: BORDER.dark, background: COLOR.black, color: COLOR.white, cursor: "pointer",
                                    }}
                                >서재에 추가</button>
                            )}
                        </div>
                    </div>
                ) : (
                    // ✅ 내 서재에서 진입: 리뷰 보기 + 리뷰 쓰기/수정 + 달력
                    <div
                        style={{
                            position: "absolute",
                            left: spx(BASE.MARGIN_X),
                            top: spx(topBtn),
                            width: spx(BASE.BLOCK_W),
                            height: spx(BASE.BTN_H),
                        }}
                    >
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: `${spx(BASE.BTN_W)} ${spx(BASE.BTN_W)} ${spx(BASE.BTN_W)}`,
                                gridColumnGap: spx(BTN_GAP),
                                height: "100%",
                            }}
                        >
                            {/* 리뷰 보기 */}
                            <button
                                onClick={async () => {
                                    const id = await ensureBookId();
                                    if (id) nav(`/books/${id}/reviews`);
                                }}
                                style={{
                                    height: "100%", fontSize: sfpx(40), fontWeight: FW.extraBold, borderRadius: RADIUS.md,
                                    border: BORDER.light, background: COLOR.white, color: COLOR.text, cursor: "pointer",
                                }}
                            >리뷰 보기</button>

                            {/* 리뷰 쓰기/수정 */}
                            {hasMyReview ? (
                                <button
                                    onClick={() => {
                                        if (!isLoggedIn()) {
                                            nav("/auth/need-login", { state: { from: location.pathname, featureName: "리뷰" } });
                                            return;
                                        }
                                        if (myReviewId) nav(`/reviews/${myReviewId}/edit`, { state: { backTo: location.pathname, hideButtons: true } });
                                    }}
                                    style={{
                                        height: "100%", fontSize: sfpx(40), fontWeight: FW.extraBold, borderRadius: RADIUS.md, transform: "translateZ(0)",
                                        border: BORDER.light, background: COLOR.white, color: COLOR.text, cursor: "pointer",
                                    }}
                                >리뷰 수정</button>
                            ) : (
                                <button
                                    onClick={async () => {
                                        if (!isLoggedIn()) {
                                            nav("/auth/need-login", { state: { from: location.pathname, featureName: "리뷰" } });
                                            return;
                                        }
                                        const id = await ensureBookId();
                                        if (id) nav(`/reviews/new?bookId=${id}`, { state: { backTo: `/books/${id}`, hideButtons: true } });
                                    }}
                                    style={{
                                        height: "100%", fontSize: sfpx(40), fontWeight: FW.extraBold, borderRadius: RADIUS.md, transform: "translateZ(0)",
                                        border: BORDER.light, background: COLOR.white, color: COLOR.text, cursor: "pointer",
                                    }}
                                >리뷰 쓰기</button>
                            )}

                            {/* 달력 추가/수정 */}
                            <button
                                  onClick={async () => {
                                    if (!isLoggedIn()) {
                                        nav("/auth/need-login", { state: { from: location.pathname, featureName: "달력" } });
                                        return;
                                    }
                                    const bId = await ensureBookId();
                                    if (!bId) return;
                                    setPickDate(calAssigned.date || isoToday());
                                    setDateOpen(true);
                                  }}
                                  style={{
                                    height: "100%", fontSize: sfpx(40), fontWeight: FW.extraBold, borderRadius: RADIUS.md, transform: "translateZ(0)",
                                    border: BORDER.dark, background: COLOR.black, color: COLOR.white, cursor: "pointer",
                                  }}
                                >
                                  {calAssigned.assigned ? "달력 수정" : "달력 추가"}
                                </button>
                        </div>
                    </div>
                )}
            </div>
            {/* 바텀시트: 서재에 추가 (읽기 상태 + 구매처) */}
            <LibrarySheet
                mode="add"
                open={!hideButtons && sheetOpen}
                stores={stores}
                loading={storesLoading}
                onClose={closeSheet}
                onSubmit={(status, storeId) => { closeSheet(); addAndGo(status, storeId); }}
            />

            {/* 일반/e-ink 앱이 둘 다 등록된 구매처의 칩을 눌렀을 때 */}
            <ActionSheet
                open={appPickOpen}
                onClose={() => setAppPickOpen(false)}
                title={`${libInfo.store?.name ?? "구매처"} 앱 선택`}
                actions={[
                    // e-ink를 위에 둔다 — 이 시트를 보는 건 대부분 e-ink 단말 사용자다.
                    { label: "e-ink 앱으로 열기", onClick: () => storePkgEink && launchStoreApp(storePkgEink) },
                    { label: "일반 앱으로 열기", onClick: () => storePkg && launchStoreApp(storePkg) },
                ]}
            />

            <CalendarPickModal
              open={dateOpen}
              currentBookId={Number(book?.id || 0)}
              currentDate={calAssigned.date || null}
              onPick={(iso)=>{ addToCalendar(iso); }}
              onClose={()=>setDateOpen(false)}
              s={s}
              spx={spx}
              sfpx={sfpx}
            />

            {/* 카드 저장 모달 */}
            <ReviewCardModal
                open={cardModalOpen}
                onClose={() => setCardModalOpen(false)}
                review={{
                    id: myReviewId ?? 0,
                    content: reviewOverride ?? "",
                    rating: myRating,
                    keywords: myKeywords,
                    created_at: myCreatedAt,
                    Book: {
                        title: book?.title ?? "",
                        cover_url: book?.cover_url ?? "",
                    },
                }}
                nickname={cardNickname}
            />
        </PageShell>
    );
}
